import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faBoxOpen,
  faCartShopping,
  faReceipt,
} from '@fortawesome/free-solid-svg-icons';
import Loader from '../Loader/Loader';
import './MisCompras.css';

function MisCompras() {
  const [compras, setCompras] = useState([]);
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const formatoPrecio = (valor) => new Intl.NumberFormat('es-AR', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
    style: 'currency',
    currency: 'ARS',
  }).format(Number(valor || 0));

  const totalesCarrito = useMemo(() => productos.reduce((acc, producto) => {
    const cantidad = Number(producto.cant || 0);
    const precio = Number(producto.precio || 0);
    return {
      cantidad: acc.cantidad + cantidad,
      precio: acc.precio + (precio * cantidad),
    };
  }, { cantidad: 0, precio: 0 }), [productos]);

  const totalHistorico = useMemo(() => compras.reduce(
    (acc, compra) => acc + Number(compra.total_precio || 0),
    0,
  ), [compras]);

  useEffect(() => {
    const cargarCompras = async () => {
      try {
        setLoading(true);
        setErrorMessage('');

        const response = await fetch('/api/mis-compras', {
          method: 'GET',
          credentials: 'include',
        });

        if (response.status === 401) {
          window.location.href = '/login';
          return;
        }

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || 'No pudimos cargar tus compras.');
        }

        setCompras(Array.isArray(data.compras) ? data.compras : []);
        setProductos(Array.isArray(data.carrito?.avisos) ? data.carrito.avisos : []);
      } catch (error) {
        console.error('Error al obtener compras:', error);
        setErrorMessage(error.message);
      } finally {
        setLoading(false);
      }
    };

    cargarCompras();
  }, []);

  const formatearFecha = (fecha) => {
    if (!fecha) return 'Fecha no informada';
    return new Intl.DateTimeFormat('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(fecha));
  };

  const etiquetaEstado = (estado) => ({
    pendiente_pago: 'pendiente de pago',
    confirmado: 'confirmado',
    entregado: 'entregado',
    cancelado: 'cancelado',
  }[estado] || estado || 'pendiente');

  if (loading) {
    return (
      <section className="mis-compras-panel">
        <div className="mis-compras-loading">
          <Loader />
          <p>Cargando tus compras</p>
        </div>
      </section>
    );
  }

  return (
    <section className="mis-compras-panel">
      <div className="mis-compras-header">
        <div>
          <p>Mi cuenta</p>
          <h2>Mis Compras</h2>
        </div>
        <Link to="/tucarrito" className="mis-compras-primary">
          Ver carrito
          <FontAwesomeIcon icon={faArrowRight} />
        </Link>
      </div>

      {errorMessage && <p className="mis-compras-message error">{errorMessage}</p>}

      <div className="mis-compras-summary">
        <article>
          <span><FontAwesomeIcon icon={faReceipt} /></span>
          <small>Compras cerradas</small>
          <strong>{compras.length}</strong>
        </article>
        <article>
          <span><FontAwesomeIcon icon={faCartShopping} /></span>
          <small>Carrito actual</small>
          <strong>{totalesCarrito.cantidad}</strong>
        </article>
        <article>
          <span><FontAwesomeIcon icon={faBoxOpen} /></span>
          <small>Total historico</small>
          <strong>{formatoPrecio(totalHistorico)}</strong>
        </article>
      </div>

      {compras.length > 0 && (
        <div className="mis-compras-history">
          <div className="mis-compras-current-title">
            <h3>Historial</h3>
            <p>Compras registradas para tu cuenta.</p>
          </div>

          <div className="mis-compras-orders">
            {compras.map((compra) => (
              <article className="mis-compras-order" key={compra._id}>
                <div className="mis-compras-order-head">
                  <div>
                    <strong>Compra #{compra._id.slice(-8).toUpperCase()}</strong>
                    <p>{formatearFecha(compra.created_at)}</p>
                  </div>
                  <span>{etiquetaEstado(compra.estado)}</span>
                </div>

                <div className="mis-compras-items compact">
                  {(compra.avisos || []).map((producto, index) => {
                    const avisoid = producto.avisoid;
                    const titulo = producto.titulo || `Producto #${avisoid}`;
                    const cantidad = Number(producto.cant || 0);
                    const precio = Number(producto.precio || 0);

                    return (
                      <article className="mis-compras-item" key={`${compra._id}-${avisoid}-${index}`}>
                        <Link to={`/aviso/${avisoid}`} className="mis-compras-item-media" aria-label={titulo}>
                          {producto.imagen ? <img src={producto.imagen} alt={titulo} /> : <span>{avisoid}</span>}
                        </Link>
                        <div>
                          <Link to={`/aviso/${avisoid}`} className="mis-compras-item-title">{titulo}</Link>
                          <p>{cantidad} unidad{cantidad === 1 ? '' : 'es'}</p>
                        </div>
                        <strong>{formatoPrecio(precio * cantidad)}</strong>
                      </article>
                    );
                  })}
                </div>

                <div className="mis-compras-order-total">
                  <span>Total</span>
                  <strong>{formatoPrecio(compra.total_precio)}</strong>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}

      {productos.length > 0 ? (
        <div className="mis-compras-current">
          <div className="mis-compras-current-title">
            <h3>Compra en curso</h3>
            <p>Estos productos estan guardados en tu carrito.</p>
          </div>

          <div className="mis-compras-items">
            {productos.map((producto, index) => {
              const avisoid = producto.avisoid;
              const titulo = producto.titulo || `Producto #${avisoid}`;
              const cantidad = Number(producto.cant || 0);
              const precio = Number(producto.precio || 0);

              return (
                <article className="mis-compras-item" key={`${avisoid}-${index}`}>
                  <Link to={`/aviso/${avisoid}`} className="mis-compras-item-media" aria-label={titulo}>
                    {producto.imagen ? <img src={producto.imagen} alt={titulo} /> : <span>{avisoid}</span>}
                  </Link>
                  <div>
                    <Link to={`/aviso/${avisoid}`} className="mis-compras-item-title">{titulo}</Link>
                    <p>{cantidad} unidad{cantidad === 1 ? '' : 'es'}</p>
                  </div>
                  <strong>{formatoPrecio(precio * cantidad)}</strong>
                </article>
              );
            })}
          </div>
        </div>
      ) : compras.length === 0 && (
        <div className="mis-compras-empty">
          <FontAwesomeIcon icon={faReceipt} />
          <h3>Todavia no tenes compras registradas</h3>
          <p>Cuando cierres una compra, vas a poder seguirla desde esta seccion.</p>
          <Link to="/productos">Explorar productos</Link>
        </div>
      )}
    </section>
  );
}

export default MisCompras;
